import { rawApi as api } from '../utils/apiWithLogging';

// Types for Code Generator
export interface CodeTemplate {
  id: number;
  entity_type: string;
  prefix: string;
  current_number: number;
  padding: number;
  format_pattern: string;
  is_active: boolean;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface CodeTemplateForm {
  entity_type: string;
  prefix: string;
  current_number: number;
  padding: number;
  format_pattern: string;
  is_active: boolean;
  description?: string;
}

export interface CodeGenerationRequest {
  entity_type: string;
  count?: number;
}

export interface CodeGenerationResponse {
  entity_type: string;
  codes: string[];
  next_number: number;
  template: CodeTemplate;
}

export interface CodePreviewRequest {
  entity_type: string;
  count?: number;
}

export interface CodePreviewResponse {
  entity_type: string;
  preview_codes: string[];
  current_number: number;
  next_number: number;
}

export interface ResetCounterRequest {
  entity_type: string;
  new_number: number;
}

export interface NextCodePreview {
  entity_type: string;
  next_code: string;
  current_number: number;
  next_number: number;
}

const codeGeneratorService = {
  // ========== Code Templates Management ==========
  
  /**
   * ดึงรายการ Code Templates ทั้งหมด
   */
  async getCodeTemplates(): Promise<CodeTemplate[]> {
    const response = await api.get<CodeTemplate[]>('/code-generator/templates');
    return response.data;
  },

  /**
   * ดึงรายการ Code Templates ทั้งหมด (alias สำหรับใช้ในหน้า Settings)
   */
  async getTemplates(): Promise<CodeTemplate[]> {
    return this.getCodeTemplates();
  },

  /**
   * ดึง Code Template ตามประเภทข้อมูล
   */
  async getCodeTemplate(entityType: string): Promise<CodeTemplate> {
    const response = await api.get<CodeTemplate>(`/code-generator/templates/${entityType}`);
    return response.data;
  },

  /**
   * สร้าง Code Template ใหม่
   */
  async createCodeTemplate(template: CodeTemplateForm): Promise<CodeTemplate> {
    const response = await api.post<CodeTemplate>('/code-generator/templates', template);
    return response.data;
  },

  /**
   * แก้ไข Code Template
   */
  async updateCodeTemplate(entityType: string, template: Partial<CodeTemplateForm>): Promise<CodeTemplate> {
    const response = await api.put<CodeTemplate>(`/code-generator/templates/${entityType}`, template);
    return response.data;
  },

  /**
   * ลบ Code Template
   */
  async deleteCodeTemplate(entityType: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/code-generator/templates/${entityType}`);
    return response.data;
  },

  // ========== Code Generation ==========

  /**
   * สร้างรหัสใหม่
   */
  async generateCode(request: CodeGenerationRequest): Promise<CodeGenerationResponse> {
    const response = await api.post<CodeGenerationResponse>('/code-generator/generate', {
      entity_type: request.entity_type,
      count: request.count || 1
    });
    return response.data;
  },

  /**
   * ดูตัวอย่างรหัสถัดไป
   */
  async previewCodes(request: CodePreviewRequest): Promise<CodePreviewResponse> {
    const response = await api.post<CodePreviewResponse>('/code-generator/preview', {
      entity_type: request.entity_type,
      count: request.count || 1
    });
    return response.data;
  },

  /**
   * ดูรหัสถัดไปที่จะถูกสร้าง (สำหรับแสดงใน UI)
   */
  async getNextCodePreview(entityType: string, forceReload: boolean = false): Promise<NextCodePreview> {
    // เพิ่ม timestamp เป็น query parameter เพื่อป้องกัน cache
    const params = forceReload ? { _t: Date.now() } : {};
    const response = await api.get<NextCodePreview>(
      `/code-generator/next-code/${entityType}`,
      { params }
    );
    return response.data;
  },

  /**
   * รีเซ็ตตัวนับ
   */
  async resetCounter(request: ResetCounterRequest): Promise<{ message: string; entity_type: string; new_number: number; next_code: string }> {
    const response = await api.post(`/code-generator/reset-counter`, request);
    return response.data;
  },

  /**
   * ดึงรายการประเภทข้อมูลที่มี
   */
  async getEntityTypes(): Promise<string[]> {
    const response = await api.get<{ entity_types: string[] }>('/code-generator/entity-types');
    return response.data.entity_types;
  },

  // ========== Helper Functions ==========

  /**
   * สร้างรหัสสำหรับ entity type เฉพาะ (สำหรับใช้ในฟอร์ม)
   */
  async generateSingleCode(entityType: string): Promise<string> {
    const response = await api.post<{ code: string; entity_type: string }>('/code-generator/generate-single', {
      entity_type: entityType
    });
    return response.data.code;
  },

  /**
   * ตรวจสอบว่า format pattern ถูกต้องหรือไม่
   */
  validateFormatPattern(pattern: string): boolean {
    try {
      // ทดสอบ format pattern
      const testCode = pattern.replace('{prefix}', 'TEST').replace('{number:05d}', '00001');
      return testCode.length > 0 && testCode.includes('TEST') && testCode.includes('00001');
    } catch {
      return false;
    }
  },

  /**
   * สร้างตัวอย่าง format pattern
   */
  generateFormatPatternExamples(): { pattern: string; example: string; description: string }[] {
    return [
      {
        pattern: '{prefix}{number:05d}',
        example: 'KS00001',
        description: 'รหัสพื้นฐาน: คำนำหน้า + เลข 5 หลัก'
      },
      {
        pattern: '{prefix}-{number:04d}',
        example: 'PRD-0001',
        description: 'รหัสแบบมีเส้นกั่น: คำนำหน้า - เลข 4 หลัก'
      },
      {
        pattern: '{prefix}{number:06d}',
        example: 'CMP000001',
        description: 'รหัสแบบยาว: คำนำหน้า + เลข 6 หลัก'
      },
      {
        pattern: '{prefix}_{number:03d}',
        example: 'DEV_001',
        description: 'รหัสแบบมี underscore: คำนำหน้า _ เลข 3 หลัก'
      }
    ];
  }
};

export default codeGeneratorService;